import { Link } from "react-router-dom";
import { Lock, Truck, UserRound } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useStoreConfig } from "@/hooks/useProducts";
import { formatShortEUR } from "@/lib/format";
import { openCookiePreferences } from "@/lib/consent";
import { CATEGORIES } from "@/data/content";
import { COMPANY } from "@/data/company";

const INFO_LINKS = [
  { to: "/contatti", label: "Contatti", id: "contatti" },
  { to: "/termini", label: "Termini e condizioni", id: "termini" },
  { to: "/privacy", label: "Privacy policy", id: "privacy" },
  { to: "/cookie", label: "Cookie policy", id: "cookie" },
];
const linkCls = "text-[16px] text-ivory/85 transition-colors hover:text-copper-light";
const headCls = "text-[13px] uppercase tracking-[0.25em] text-copper-light";

const Service = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-4">
    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-copper-light/40 text-copper-light">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
    </span>
    <div>
      <p className="font-display text-2xl text-ivory">{title}</p>
      <p className="mt-1 text-[15px] text-ivory/75">{text}</p>
    </div>
  </div>
);

export const Footer = () => {
  const { threshold } = useStoreConfig();
  const year = new Date().getFullYear();
  return (
    <footer className="relative overflow-hidden bg-navy text-ivory" data-testid="site-footer">
      <div className="container-pf grid gap-10 border-b border-ivory/10 py-14 md:grid-cols-3">
        <Service icon={Truck} title="Spedizione gratuita" text={`Per ordini da ${formatShortEUR(threshold)} in tutta Italia.`} />
        <Service icon={Lock} title="Pagamento sicuro" text="Carta di credito e debito tramite Stripe." />
        <Service icon={UserRound} title="Nessuna registrazione" text="Acquisti come ospite, in pochi passaggi." />
      </div>
      <div className="container-pf grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-12">
        <div className="sm:col-span-2 lg:col-span-4">
          <Logo light />
          <p className="mt-6 max-w-sm font-display text-2xl italic leading-snug text-ivory/85">La cura quotidiana per una vita più bella a ogni età.</p>
        </div>
        <div className="lg:col-span-2">
          <p className={headCls}>Collezione</p>
          <ul className="mt-5 space-y-3">
            {CATEGORIES.map((c) => (
              <li key={c}>
                <Link to={c === "Tutti" ? "/collezione" : `/collezione?categoria=${encodeURIComponent(c)}`} data-testid={`footer-cat-${c.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                  {c === "Tutti" ? "Tutti i prodotti" : c}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className={headCls}>Informazioni</p>
          <ul className="mt-5 space-y-3">
            {INFO_LINKS.map((l) => (
              <li key={l.id}><Link to={l.to} data-testid={`footer-link-${l.id}`} className={linkCls}>{l.label}</Link></li>
            ))}
            <li>
              <button type="button" onClick={openCookiePreferences} data-testid="footer-cookie-preferences" className={linkCls}>Preferenze cookie</button>
            </li>
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className={headCls}>Assistenza</p>
          <ul className="mt-5 space-y-3 text-[16px] text-ivory/85">
            <li><a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className={`${linkCls} break-all`}>{COMPANY.email}</a></li>
            <li><a href={COMPANY.phoneHref} data-testid="footer-phone" className={linkCls}>{COMPANY.phone}</a></li>
            <li data-testid="footer-address">{COMPANY.street}<br />{COMPANY.cityLine}</li>
          </ul>
        </div>
      </div>
      <p aria-hidden="true" className="pointer-events-none select-none whitespace-nowrap text-center font-brand text-[21vw] leading-[0.78] text-ivory/[0.06]">
        PERFERO
      </p>
      <div className="container-pf flex flex-col gap-3 border-t border-ivory/10 py-6 text-[13px] text-ivory/70 sm:flex-row sm:items-center sm:justify-between">
        <p data-testid="footer-company">© {year} {COMPANY.name} · Sede legale {COMPANY.address} · C.F. e P.IVA {COMPANY.vat} · REA {COMPANY.rea}</p>
        <Link to="/admin/login" data-testid="footer-admin-link" className="transition-colors hover:text-copper-light">
          Area riservata
        </Link>
      </div>
    </footer>
  );
};
